import type { OrgModelRecord, OrgModelType } from "@/app/providers/services";
import dagre, { graphlib } from "@dagrejs/dagre";
import { ConnectionLineType, MarkerType, type Edge, type Node } from "@xyflow/react";
import { TYPE_OPTIONS, type FormMode, type FormState, type OrgModelEntityNodeData, type OrgModelRecordNodeData } from "./types";

export const CREATE_TYPE_OPTIONS: OrgModelType[] = ["parent", "approver", "member", "delegate"];

const SUBJECT_ID_LABELS_BY_TYPE: Partial<Record<OrgModelType, string>> = {
  member: "Member Of",
  parent: "Child",
  delegate: "Contact",
  approver: "Subject ID",
};

const RELATED_ID_LABELS_BY_TYPE: Partial<Record<OrgModelType, string>> = {
  member: "Contact ID",
  parent: "Parent",
  delegate: "Delegate",
  approver: "Related Contact",
};

const ORG_MODEL_SUMMARY_TYPES: OrgModelType[] = ["member", "parent", "delegate", "approver"];

const parseDate = (value?: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

export const getIssueCyclePath = (record: OrgModelRecord): string => {
  return (record.cyclePath || []).join(" -> ") || "-";
};

export const getIssueMessage = (record: OrgModelRecord): string => {
  return record.message || "Hierarchy issue detected.";
};

export const getRecordListSummary = (record: OrgModelRecord): string => {
  if (record.type === "hierarchyIssue") {
    const cycle = (record.cyclePath || []).join(" -> ");
    return `${record.type} | ${record.subjectType}:${record.subjectId} | ${record.issueType || "issue"}${cycle ? ` | ${cycle}` : ""}`;
  }
  if (record.type === "hierarchySummary") {
    return `${record.type} | ${record.subjectType}:${record.subjectId}`;
  }
  return `${record.type} | ${record.subjectType}:${record.subjectId} -> ${record.relatedId || "-"}`;
};

export const getInitialFormState = (mode: FormMode, record?: OrgModelRecord | null): FormState => {
  if (!record) {
    return {
      externalId: "",
      type: "parent",
      subjectType: "",
      subjectId: "",
      relatedId: "",
      validFrom: null,
      validTo: null,
      level: "",
      source: "",
    };
  }

  return {
    externalId: "",
    type: record.type,
    subjectType: record.subjectType || "",
    subjectId: record.subjectId || "",
    relatedId: record.relatedId || "",
    validFrom: parseDate(record.validFrom),
    validTo: parseDate(record.validTo),
    level: record.level !== undefined && record.level !== null ? String(record.level) : "",
    source: (record.source as FormState["source"]) || "",
  };
};

export const getOrgModelFormSubjectIdLabel = (type: OrgModelType): string => {
  return SUBJECT_ID_LABELS_BY_TYPE[type] ?? "Subject ID";
};

export const getOrgModelFormRelatedIdLabel = (type: OrgModelType): string => {
  return RELATED_ID_LABELS_BY_TYPE[type] ?? "Related ID";
};

export const buildOrgModelRelationSummary = (
  type: OrgModelType,
  subjectType: string,
  subjectId: string,
  relatedId: string,
): string | null => {
  if (!ORG_MODEL_SUMMARY_TYPES.includes(type)) {
    return null;
  }
  const subj = subjectId.trim() || "…";
  const rel = relatedId.trim() || "…";
  const subjectKind = subjectType.trim() || "…";
  switch (type) {
    case "member":
      return `${rel} is a member of the ${subjectKind} ${subj}`;
    case "parent":
      return `${rel} is the parent ${subjectKind} of ${subj}`;
    case "delegate":
      return `${rel} is the delegate of ${subj}`;
    case "approver":
      return `${rel} is the approver of ${subjectKind} ${subj}`;
    default:
      return null;
  }
};

export const mapFormStateToCreatePayload = (state: FormState) => ({
  ...(state.externalId.trim() ? { externalId: state.externalId.trim() } : {}),
  type: state.type as "parent" | "approver" | "member" | "delegate",
  subjectType: state.subjectType.trim(),
  subjectId: state.subjectId.trim(),
  relatedId: state.relatedId.trim(),
  ...(state.validFrom ? { validFrom: state.validFrom.toISOString() } : {}),
  ...(state.validTo ? { validTo: state.validTo.toISOString() } : {}),
});

export const mapFormStateToUpdatePayload = (state: FormState) => {
  const payload: Record<string, unknown> = {
    type: state.type,
    subjectType: state.subjectType.trim(),
    subjectId: state.subjectId.trim(),
    relatedId: state.relatedId.trim(),
  };

  if (state.validFrom) payload.validFrom = state.validFrom.toISOString();
  if (state.validTo) payload.validTo = state.validTo.toISOString();
  if (state.level.trim()) payload.level = Number(state.level);
  if (state.source) payload.source = state.source;

  return payload;
};

// --- Graph layout helpers -------------------------------------------------

// Dimensions are kept as multiples of 4px so the matching Tailwind size classes
// on the node components (w-*, min-h-*) stay on the whole-number spacing scale.
const NODE_WIDTH = 340;
const NODE_HEIGHT = 248;
const ISSUE_NODE_WIDTH = 372;
const ISSUE_NODE_HEIGHT = 172;
const ENTITY_NODE_WIDTH = 280;
const ENTITY_NODE_HEIGHT = 120;

export const getRecordNodeId = (record: OrgModelRecord) =>
  `record:${record.id || `${record.type}:${record.subjectType}:${record.subjectId}:${record.relatedId}`}`;

export const getEntityNodeId = (entityType: string, entityId: string) => `entity:${entityType}:${entityId}`;

export const getRelatedEntityType = (record: OrgModelRecord): string | null => {
  if (record.type === "hierarchySummary") return null;
  if (record.type === "hierarchyIssue") return null;
  if (record.type === "parent") return record.subjectType;
  if (
    record.type === "approver" ||
    record.type === "member" ||
    record.type === "delegate" ||
    record.type === "resolvedApprover"
  ) {
    return "contact";
  }
  return "unknown";
};

export const asString = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

export const truncateWithEllipsis = (value: string, maxLength: number) => {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}...`;
};

const getContactDisplayLabel = (
  contact: OrgModelRecord["relatedContact"] | OrgModelRecord["subjectContact"] | undefined,
  fallbackId: string,
) => {
  if (!contact) return fallbackId;
  const firstName = asString(contact.firstName);
  const lastName = asString(contact.lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;

  const email = asString(contact.email);
  if (email) return email;

  return fallbackId;
};

export const getSubjectEntityLabel = (record: OrgModelRecord): string => {
  if (record.subjectType !== "contact") return record.subjectId || "unknown";
  if (record.type === "delegate") return getContactDisplayLabel(record.subjectContact, record.subjectId || "unknown");
  return record.subjectId || "unknown";
};

export const getRelatedEntityLabel = (record: OrgModelRecord): string => {
  const relatedType = getRelatedEntityType(record);
  if (!relatedType) return record.relatedId || "unknown";
  if (relatedType !== "contact") return record.relatedId || "unknown";
  return getContactDisplayLabel(record.relatedContact, record.relatedId || "unknown");
};

export const getGraphNodeDimensions = (node: Node) => {
  if (node.type !== "orgModelRecord") {
    return { width: ENTITY_NODE_WIDTH, height: ENTITY_NODE_HEIGHT };
  }
  const record = (node.data as OrgModelRecordNodeData | undefined)?.record;
  if (record?.type === "hierarchyIssue") {
    return { width: ISSUE_NODE_WIDTH, height: ISSUE_NODE_HEIGHT };
  }
  return { width: NODE_WIDTH, height: NODE_HEIGHT };
};

export const getLayoutedGraph = (
  graphRecordsById: Record<string, OrgModelRecord>,
  visibleGraphTypes: OrgModelType[],
  expandingByNodeId: Record<string, { incoming: boolean; outgoing: boolean }>,
  onExpandIncoming: (entityType: string, entityId: string) => void,
  onExpandOutgoing: (entityType: string, entityId: string) => void,
) => {
  const dagreGraph = new graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: "RL", nodesep: 45, ranksep: 65 });

  const allRecords = Object.values(graphRecordsById).filter((record) => Boolean(record.id));
  const visibleTypes = new Set(visibleGraphTypes);
  const records = allRecords.filter((record) => visibleTypes.has(record.type));
  const nodesById: Record<string, Node> = {};
  const edges: Edge[] = [];
  const edgeIds = new Set<string>();

  for (const record of records) {
    const recordNodeId = getRecordNodeId(record);
    const subjectEntityType = record.subjectType || "unknown";
    const subjectEntityId = record.subjectId || "unknown";
    const subjectEntityLabel = getSubjectEntityLabel(record);
    const relatedEntityType = getRelatedEntityType(record);
    const relatedEntityId = relatedEntityType ? record.relatedId || "unknown" : "";
    const relatedEntityLabel = relatedEntityType ? getRelatedEntityLabel(record) : "";

    const subjectNodeId = getEntityNodeId(subjectEntityType, subjectEntityId);
    const relatedNodeId = relatedEntityType ? getEntityNodeId(relatedEntityType, relatedEntityId) : "";
    const subjectExpanding = expandingByNodeId[subjectNodeId] || { incoming: false, outgoing: false };
    const relatedExpanding = relatedNodeId
      ? expandingByNodeId[relatedNodeId] || { incoming: false, outgoing: false }
      : { incoming: false, outgoing: false };

    if (!nodesById[subjectNodeId]) {
      nodesById[subjectNodeId] = {
        id: subjectNodeId,
        type: "orgModelEntity",
        position: { x: 0, y: 0 },
        data: {
          entityType: subjectEntityType,
          entityId: subjectEntityId,
          entityLabel: subjectEntityLabel,
          expandingIncoming: subjectExpanding.incoming,
          expandingOutgoing: subjectExpanding.outgoing,
          onExpandIncoming,
          onExpandOutgoing,
        } as OrgModelEntityNodeData,
        width: ENTITY_NODE_WIDTH,
        height: ENTITY_NODE_HEIGHT,
      };
      dagreGraph.setNode(subjectNodeId, { width: ENTITY_NODE_WIDTH, height: ENTITY_NODE_HEIGHT });
    } else {
      const existing = nodesById[subjectNodeId];
      if (existing.type === "orgModelEntity") {
        const existingData = existing.data as OrgModelEntityNodeData;
        if (existingData.entityLabel === existingData.entityId && subjectEntityLabel !== existingData.entityId) {
          existing.data = { ...existingData, entityLabel: subjectEntityLabel };
        }
      }
    }

    if (!nodesById[recordNodeId]) {
      const recordNodeWidth = record.type === "hierarchyIssue" ? ISSUE_NODE_WIDTH : NODE_WIDTH;
      const recordNodeHeight = record.type === "hierarchyIssue" ? ISSUE_NODE_HEIGHT : NODE_HEIGHT;
      nodesById[recordNodeId] = {
        id: recordNodeId,
        type: "orgModelRecord",
        position: { x: 0, y: 0 },
        data: {
          record,
        } as OrgModelRecordNodeData,
        width: recordNodeWidth,
        height: recordNodeHeight,
      };
      dagreGraph.setNode(recordNodeId, { width: recordNodeWidth, height: recordNodeHeight });
    }

    if (relatedEntityType && relatedNodeId) {
      if (!nodesById[relatedNodeId]) {
        nodesById[relatedNodeId] = {
          id: relatedNodeId,
          type: "orgModelEntity",
          position: { x: 0, y: 0 },
          data: {
            entityType: relatedEntityType,
            entityId: relatedEntityId,
            entityLabel: relatedEntityLabel,
            expandingIncoming: relatedExpanding.incoming,
            expandingOutgoing: relatedExpanding.outgoing,
            onExpandIncoming,
            onExpandOutgoing,
          } as OrgModelEntityNodeData,
          width: ENTITY_NODE_WIDTH,
          height: ENTITY_NODE_HEIGHT,
        };
        dagreGraph.setNode(relatedNodeId, { width: ENTITY_NODE_WIDTH, height: ENTITY_NODE_HEIGHT });
      } else {
        const existing = nodesById[relatedNodeId];
        if (existing.type === "orgModelEntity") {
          const existingData = existing.data as OrgModelEntityNodeData;
          if (existingData.entityLabel === existingData.entityId && relatedEntityLabel !== existingData.entityId) {
            existing.data = { ...existingData, entityLabel: relatedEntityLabel };
          }
        }
      }
    }

    const addEdge = (source: string, target: string) => {
      const edgeId = `${source}->${target}`;
      if (edgeIds.has(edgeId)) return;
      edgeIds.add(edgeId);
      edges.push({
        id: edgeId,
        source,
        target,
        type: ConnectionLineType.SmoothStep,
        animated: false,
        zIndex: 5,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#6b7280",
        },
        style: {
          stroke: "#6b7280",
          strokeWidth: 1.5,
        },
      });
      dagreGraph.setEdge(source, target);
    };

    if (relatedEntityType && relatedNodeId) {
      addEdge(relatedNodeId, recordNodeId);
    }
    addEdge(recordNodeId, subjectNodeId);
  }

  dagre.layout(dagreGraph);

  const nodes = Object.values(nodesById).map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id) || { x: 0, y: 0 };
    const { width, height } = getGraphNodeDimensions(node);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return {
    nodes,
    edges,
    visibleRecordCount: records.length,
    hiddenRecordCount: allRecords.length - records.length,
  };
};

export const getListData = (response: any): OrgModelRecord[] => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};
